import {
  Box,
  HStack,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
  useDisclosure,
} from '@chakra-ui/react';
import { Fragment, useEffect, useMemo, useState } from 'react';
import { format, parseISO } from 'date-fns';
import formatNumber from 'app/utils/formatting';

import {
  ActionIconButton,
  ArrowDownBold,
  DEFAULT_DATE_FORMAT,
  DocumentDownloadOutline,
  EyeOutline,
  PointsTransaction,
  SortArrowsAscBold,
  SortArrowsDescBold,
  useToast,
} from '@aslanpay/portal-common-lib';

import BulkGiftingDetailsModal from 'app/components/Gifting/BulkGiftingDetailsModal';

import useFetchEmployees from 'app/hooks/useFetchEmployees';
import useDownloadGiftingReports from 'app/hooks/useDownloadGiftingReports';

import { EmployeesGiftedRow } from 'app/components/UI/Table/Table.types';

import { useQueryClient } from '@tanstack/react-query';
import { PaginationState } from '@tanstack/react-table';

import getPointsValue from 'app/utils/points';

import { RewardHistoryTableData } from './types';
import { buildGiftingExportParams, UNGROUPED_GROUP_ID_PREFIX } from './exportParams';

type Props = {
  transactions: PointsTransaction[];
  index: number;
};

const PAGE_SIZE = 50;

function RewardHistoryTable({ transactions, index }: Props) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [selectedRow, setSelectedRow] = useState<RewardHistoryTableData | null>(null);
  const [employeesAdaptedToModal, setEmployeesAdaptedToModal] = useState<EmployeesGiftedRow[]>([]);
  const [page, setPage] = useState<number>(1);
  const [employeesPagination, setEmployeesPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 15,
  });
  const [order, setOrder] = useState<string>('DESC');

  // Download hook for gifting reports
  const { mutate: downloadGiftingReports } = useDownloadGiftingReports({
    onSuccess: () => {
      toast({
        position: 'top-right',
        status: 'success',
        title: 'Gifting report downloaded successfully',
      });
    },
    onError: () => {
      toast({
        position: 'top-right',
        status: 'error',
        title: 'Something went wrong',
        description: 'Sorry, it was not possible to download the file. Please try again.',
      });
    },
  });

  useEffect(() => {
    if (index === 0) {
      setPage(1);
    }
  }, [index]);

  const { pageIndex, pageSize } = employeesPagination;
  const startIndex = pageIndex * pageSize;
  const employeeIds =
    selectedRow?.childTransactions
      ?.slice(startIndex, startIndex + pageSize)
      .map((tx) => tx.individualId) || [];

  const { data: employeesData, isLoading: isLoadingEmployeesData } = useFetchEmployees(
    {
      ids: employeeIds,
      page: 0,
      size: employeeIds.length,
    },
    {
      enabled: !!selectedRow && employeeIds.length > 0,
    }
  );

  const {
    isOpen: isBulkGiftingDetailsOpen,
    onOpen: onOpenBulkGiftingDetails,
    onClose: onCloseBulkGiftingDetails,
  } = useDisclosure();

  const rowClicked = (groupedPointsTransaction: RewardHistoryTableData) => {
    setSelectedRow(groupedPointsTransaction);

    onOpenBulkGiftingDetails();
  };

  useEffect(() => {
    if (employeesData) {
      const employeesToShow = employeesData.list
        .map((emp) => ({
          fullName: `${emp.firstName} ${emp.lastName}`,
          email: emp.email,
          points: selectedRow?.childTransactions[0].amount ?? 0,
        }))
        .sort((a, b) => a.fullName.localeCompare(b.fullName));

      setEmployeesAdaptedToModal(employeesToShow);
    }
  }, [employeesData, selectedRow?.childTransactions, setEmployeesAdaptedToModal]);

  const onCloseDetailsModal = () => {
    setEmployeesPagination({ pageIndex: 0, pageSize: 15 });
    setEmployeesAdaptedToModal([]);
    setSelectedRow(null);
    queryClient.removeQueries({ queryKey: ['fetchEmployees'] });
    onCloseBulkGiftingDetails();
  };

  const sortClicked = () => {
    if (order === 'ASC') {
      setOrder('DESC');
    }

    if (order === 'DESC') {
      setOrder('ASC');
    }
  };

  const groupedTransactions = useMemo(() => {
    const rawTransactions =
      order === 'ASC'
        ? [...transactions].sort(
            (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
          )
        : [...transactions].sort(
            (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
          );

    const grouped = rawTransactions.reduce((acc, transaction) => {
      let groupId = transaction.transactionGroupId;

      // If no group ID, create a unique one based on timestamp and labelName (matching client portal)
      if (!groupId) {
        const timestamp = new Date(transaction.createdAt).getTime();
        const labelName = transaction.labelName || '';
        // Create a unique group ID based on timestamp (within 1 minute) and labelName
        const timeWindow = Math.floor(timestamp / 60000); // Group by minute
        groupId = `${UNGROUPED_GROUP_ID_PREFIX}${timeWindow}_${labelName
          .substring(0, 20)
          .replace(/[^a-zA-Z0-9]/g, '')}`;
      }

      const date = format(parseISO(transaction.createdAt), DEFAULT_DATE_FORMAT);
      const labelName = transaction.labelName || 'Employee Gift';
      const notes = transaction.notes || '';

      const reason = labelName.length > 20 ? `${labelName.substring(0, 20)}...` : labelName;

      if (!acc[groupId]) {
        acc[groupId] = {
          groupId,
          date,
          rawDate: transaction.createdAt, // Store raw ISO date for modal
          points: 0,
          cost: 0,
          childTransactions: [],
          reason,
          reasonText: notes,
          id: groupId,
          transactionIds: [],
          individualIds: [],
          recipients: 0,
        };
      }

      acc[groupId].points += transaction.amount;
      acc[groupId].cost += getPointsValue(transaction.amount);
      acc[groupId].recipients += 1;
      acc[groupId].childTransactions.push(transaction);
      acc[groupId].transactionIds.push(transaction.id);
      acc[groupId].individualIds.push(transaction.individualId);

      return acc;
    }, {} as Record<string, RewardHistoryTableData>);

    return Object.values(grouped).sort((a, b) => {
      const aTime: number = new Date(a.childTransactions[0].createdAt).getTime();
      const bTime: number = new Date(b.childTransactions[0].createdAt).getTime();
      return order === 'ASC' ? aTime - bTime : bTime - aTime;
    });
  }, [transactions, order]);

  const handleDownload = (groupedTransaction: RewardHistoryTableData) => {
    downloadGiftingReports(buildGiftingExportParams(groupedTransaction));
  };

  return (
    <Box overflowX="auto" w="full" maxH="600px">
      <Table variant="simple" size="sm">
        <Thead>
          <Tr>
            <Th onClick={sortClicked}>
              Date{' '}
              {order === 'DESC' ? (
                <SortArrowsDescBold color="brand.purple" boxSize="15px" />
              ) : (
                <SortArrowsAscBold color="brand.purple" boxSize="15px" />
              )}
            </Th>
            <Th>No. of points awarded</Th>
            <Th>Recipients</Th>
            <Th>Reason</Th>
            <Th />
            <Th />
          </Tr>
        </Thead>
        <Tbody>
          {groupedTransactions.slice(0, PAGE_SIZE * page).map((tx) => {
            return (
              <Fragment key={tx.id}>
                <Tr onClick={() => rowClicked(tx)} _hover={{ bg: 'gray.50', cursor: 'pointer' }}>
                  <Td>{tx.date}</Td>
                  <Td>{formatNumber(tx.points)}</Td>
                  <Td>{tx.childTransactions.length}</Td>
                  <Td>{tx.reason}</Td>
                  <Td textAlign="right" onClick={(e) => e.stopPropagation()}>
                    <EyeOutline onClick={() => rowClicked(tx)} boxSize={6} color="brand.purple" />
                  </Td>
                  <Td textAlign="right" onClick={(e) => e.stopPropagation()}>
                    <Tooltip label="Download report" placement="top">
                      <ActionIconButton
                        aria-label="Download report"
                        icon={<DocumentDownloadOutline />}
                        onClick={() => handleDownload(tx)}
                        color="brand.purple"
                        _hover={{ bg: 'brand.lightGray2' }}
                      />
                    </Tooltip>
                  </Td>
                </Tr>
              </Fragment>
            );
          })}
        </Tbody>
      </Table>
      {groupedTransactions.length > PAGE_SIZE * page && (
        <Box
          w="full"
          borderRadius="8px"
          border="1px solid"
          borderColor="brand.purple"
          px="20px"
          py="10px"
          mt="32px"
          onClick={() => setPage(page + 1)}
          _hover={{
            cursor: 'pointer',
          }}
        >
          <HStack justifyContent="center">
            <ArrowDownBold boxSize={6} color="brand.purple" />
            <Text color="brand.purple" textStyle="smallMedium">
              See More
            </Text>
          </HStack>
        </Box>
      )}
      <BulkGiftingDetailsModal
        isOpen={isBulkGiftingDetailsOpen}
        onClose={onCloseDetailsModal}
        employees={employeesAdaptedToModal}
        showRewardDetails
        rewardDetails={selectedRow || undefined}
        isLoadingEmployeesData={isLoadingEmployeesData}
        totalEmployees={selectedRow?.childTransactions.length || 0}
        pagination={employeesPagination}
        onPaginationChange={setEmployeesPagination}
      />
    </Box>
  );
}

export default RewardHistoryTable;
